import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import type { BrandMember, BrandRole } from "@/hooks/use-members";
import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";

const ROLES: BrandRole[] = ["OWNER", "ADMIN", "CREATOR", "REVIEWER", "SCHEDULER", "VIEWER"];

interface Props {
  member: BrandMember;
  currentUserId: string;
  canEdit: boolean;
  onRoleChange: (role: BrandRole) => void;
  onRemove: () => void;
}

export function MemberRow({ member, currentUserId, canEdit, onRoleChange, onRemove }: Props) {
  const { t } = useTranslation("settings");
  const locale = useLocale();
  const isSelf = member.userId === currentUserId;
  const locked = isSelf || !canEdit;
  return (
    <div className="flex items-center gap-3 rounded-lg border p-3 md:grid md:grid-cols-[auto_1fr_auto_auto_auto] md:items-center">
      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-muted">
        {member.avatarUrl ? (
          <img src={member.avatarUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sm font-medium">
            {(member.name ?? member.email)[0]?.toUpperCase()}
          </div>
        )}
      </div>
      <div className="min-w-0">
        <div className="truncate font-medium">{member.name ?? member.email}</div>
        <div className="truncate text-xs text-muted-foreground">{member.email}</div>
      </div>
      <select
        value={member.role}
        disabled={locked}
        onChange={(e) => onRoleChange(e.target.value as BrandRole)}
        className="rounded border px-2 py-1 text-sm disabled:opacity-60"
      >
        {ROLES.map((r) => <option key={r} value={r}>{t(`team.roles.${r}`)}</option>)}
      </select>
      <div className="text-xs text-muted-foreground">
        {member.lastActiveAt ? formatDate(new Date(member.lastActiveAt), locale, { dateStyle: "medium" }) : "—"}
      </div>
      <Button
        size="sm"
        variant="ghost"
        disabled={locked}
        onClick={onRemove}
        aria-label={t("team.removeAriaLabel", { email: member.email })}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
